import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import "./AdminVoucher.css";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminVouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const { userInfo, authFetch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isAddMode, setIsAddMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentVoucher, setCurrentVoucher] = useState(null);
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [isPercentage, setIsPercentage] = useState(false);
  const [maxUsageCount, setMaxUsageCount] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [minimumSpendAmount, setMinimumSpendAmount] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);

  const [availableList, setAvailableList] = useState([]);
  const [selectedList, setSelectedList] = useState([]);
  const [highlightedAvailable, setHighlightedAvailable] = useState([]);
  const [highlightedSelected, setHighlightedSelected] = useState([]);

  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [voucherSearch, setVoucherSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");

  useEffect(() => {
    const fetchVouchers = async () => {
      try {
        const response = await authFetch(`${API_BASE_URL}/api/Vouchers`);
        if (response.ok) {
          const data = await response.json();
          setVouchers(data);
          setFilteredVouchers(data);
        } else {
          setError("Failed to fetch vouchers");
        }
      } catch (err) {
        setError("Error fetching vouchers: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/Products`);
        if (response.ok) {
          const data = await response.json();
          setAvailableProducts(data.products);
        }
      } catch (err) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      }
    };

    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
        }
      } catch (err) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchVouchers();
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    let result = vouchers;

    if (voucherSearch.trim() !== '') {
      const searchTerm = voucherSearch.toLowerCase();
      result = result.filter(voucher =>
        voucher.code.toLowerCase().includes(searchTerm) ||
        voucher.description.toLowerCase().includes(searchTerm)
      );
    }

    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      result = result.filter(voucher => voucher.isActive === isActive);
    }

    setFilteredVouchers(result);
  }, [voucherSearch, statusFilter, vouchers]);

  useEffect(() => {
    if (availableProducts.length > 0) {
      updateProductLists();
    }
  }, [availableProducts, selectedProducts, nameFilter, categoryFilter]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);

  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonColor: '#45a049',
      timer: 1500
    });
  };

  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error',
      text: message,
      icon: 'error',
      confirmButtonColor: '#e74c3c'
    });
  };

  const updateProductLists = () => {
    let available = availableProducts.filter(
      (p) => !selectedProducts.includes(p.productID)
    );

    if (nameFilter.trim()) {
      available = available.filter((p) =>
        p.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      available = available.filter((p) => p.categoryID == categoryFilter);
    }

    available.sort((a, b) => a.name.localeCompare(b.name));
    const selected = availableProducts.filter((p) =>
      selectedProducts.includes(p.productID)
    );
    selected.sort((a, b) => a.name.localeCompare(b.name));
    setAvailableList(available);
    setSelectedList(selected);
  };

  const resetForm = () => {
    setCode("");
    setDescription("");
    setDiscountAmount("");
    setIsPercentage(false);
    setMaxUsageCount("");
    setStartDate("");
    setEndDate("");
    setMinimumSpendAmount("");
    setSelectedProducts([]);
    setCurrentVoucher(null);
    setHighlightedAvailable([]);
    setHighlightedSelected([]);
    setNameFilter("");
    setCategoryFilter("");
  };

  const handleAddClick = () => {
    resetForm();
    setIsAddMode(true);
    setIsEditMode(false);
    setShowModal(true);
  };

  const handleEditClick = (voucher) => {
    setCurrentVoucher(voucher);
    setCode(voucher.code);
    setDescription(voucher.description);
    setDiscountAmount(voucher.discountAmount);
    setIsPercentage(voucher.isPercentage);
    setMaxUsageCount(voucher.maxUsageCount);
    setMinimumSpendAmount(voucher.minimumSpendAmount || "");

    if (voucher.startDate) {
      const startDateObj = new Date(voucher.startDate);
      setStartDate(toLocalDateString(startDateObj));
    } else {
      setStartDate("");
    }

    if (voucher.endDate) {
      const endDateObj = new Date(voucher.endDate);
      setEndDate(toLocalDateString(endDateObj));
    } else {
      setEndDate("");
    }

    const eligibleProductIds =
      voucher.eligibleProducts?.map((ep) => ep.productId) || [];
    setSelectedProducts(eligibleProductIds);

    setNameFilter("");
    setCategoryFilter("");

    setIsEditMode(true);
    setIsAddMode(false);
    setShowModal(true);
  };

  const handleCancelClick = () => {
    setIsAddMode(false);
    setIsEditMode(false);
    resetForm();
    setShowModal(false);
  };

  const handleAddProductClick = () => {
    if (highlightedAvailable.length === 0) return;

    const itemsToMove = availableList.filter((item) =>
      highlightedAvailable.includes(item.productID)
    );

    const newSelectedIds = [
      ...selectedProducts,
      ...itemsToMove.map((item) => item.productID),
    ];
    setSelectedProducts(newSelectedIds);
    setHighlightedAvailable([]);
  };

  const handleAddAllProductsClick = () => {
    const allProductIds = [
      ...selectedProducts,
      ...availableList.map((item) => item.productID),
    ];
    setSelectedProducts(allProductIds);
    setHighlightedAvailable([]);
  };

  const handleRemoveProductClick = () => {
    if (highlightedSelected.length === 0) return;

    const newSelectedIds = selectedProducts.filter(
      (id) => !highlightedSelected.includes(id)
    );
    setSelectedProducts(newSelectedIds);
    setHighlightedSelected([]);
  };

  const handleRemoveAllProductsClick = () => {
    setSelectedProducts([]);
    setHighlightedSelected([]);
  };

  const handleAvailableItemClick = (productID, event) => {
    if (event.ctrlKey || event.metaKey) {
      setHighlightedAvailable((prev) =>
        prev.includes(productID)
          ? prev.filter((id) => id !== productID)
          : [...prev, productID]
      );
    } else {
      setHighlightedAvailable([productID]);
    }
  };

  const handleSelectedItemClick = (productID, event) => {
    if (event.ctrlKey || event.metaKey) {
      setHighlightedSelected((prev) =>
        prev.includes(productID)
          ? prev.filter((id) => id !== productID)
          : [...prev, productID]
      );
    } else {
      setHighlightedSelected([productID]);
    }
  };

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
    setHighlightedAvailable([]);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
    setHighlightedAvailable([]);
  };

  const handleClearFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setHighlightedAvailable([]);
  };

  const resetSearchFilters = () => {
    setVoucherSearch("");
    setStatusFilter("all");
  };

  const validateForm = () => {
    if (!code.trim()) return "Voucher code is required";
    if (!description.trim()) return "Description is required";
    if (
      !discountAmount ||
      isNaN(discountAmount) ||
      parseFloat(discountAmount) <= 0
    )
      return "Discount amount must be a positive number";
    if (!maxUsageCount || isNaN(maxUsageCount) || parseInt(maxUsageCount) <= 0)
      return "Maximum usage count must be a positive number";
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return "End date must be after start date";
    if (minimumSpendAmount && (isNaN(minimumSpendAmount) || parseFloat(minimumSpendAmount) < 0))
      return "Minimum spend amount must be a non-negative number";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (selectedProducts.length === 0) {
      setError("Please select at least one product for this voucher");
      return;
    }

    setError("");

    const voucherData = {
      code,
      description,
      discountAmount: parseFloat(discountAmount),
      isPercentage,
      maxUsageCount: parseInt(maxUsageCount),
      startDate: startDate || null,
      endDate: endDate || null,
      minimumSpendAmount: minimumSpendAmount ? parseFloat(minimumSpendAmount) : null,
      createdByUserId: userInfo.userId,
      productIds: selectedProducts,
    };

    try {
      let url = `${API_BASE_URL}/api/Vouchers`;
      let method = "POST";

      if (isEditMode) {
        url = `${API_BASE_URL}/api/Vouchers/${currentVoucher.voucherId}`;
        method = "PUT";
        voucherData.voucherId = currentVoucher.voucherId;
        voucherData.isActive = currentVoucher.isActive;
      }

      const response = await authFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(voucherData),
      });

      if (response.ok) {
        const successMessage = isEditMode
          ? "Voucher updated successfully!"
          : "Voucher created successfully!"
        showSuccessAlert(successMessage);

        const refreshResponse = await authFetch(
          `${API_BASE_URL}/api/Vouchers`
        );
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setVouchers(refreshData);
          setFilteredVouchers(refreshData);
        }

        setIsAddMode(false);
        setIsEditMode(false);
        resetForm();
        setShowModal(false);
      } else {
        const errorData = await response.json();
        showErrorAlert(errorData.message || "Failed to save voucher");
      }
    } catch (err) {
      showErrorAlert("Error saving voucher: " + err.message);
    }
  };

  const handleToggleActive = async (voucherId, isCurrentlyActive) => {
    const result = await Swal.fire({
      title: isCurrentlyActive ? 'Deactivate Voucher?' : 'Activate Voucher?',
      text: isCurrentlyActive
        ? 'Are you sure you want to deactivate this voucher?'
        : 'Are you sure you want to activate this voucher?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isCurrentlyActive ? '#e74c3c' : '#45a049',
      cancelButtonColor: '#7a7a7a',
      confirmButtonText: isCurrentlyActive ? 'Yes' : 'Yes'
    });

    if (result.isConfirmed) {
      try {
        // For deactivation we use DELETE endpoint, for reactivation we'd need a custom endpoint
        if (isCurrentlyActive) {
          const response = await authFetch(
            `${API_BASE_URL}/api/Vouchers/${voucherId}`,
            {
              method: "DELETE",
            }
          );

          if (response.ok) {
            showSuccessAlert("Voucher deactivated successfully!");

            const updatedVouchers = vouchers.map((v) =>
              v.voucherId === voucherId ? { ...v, isActive: false } : v
            );
            setVouchers(updatedVouchers);
            setFilteredVouchers(updatedVouchers);
          } else {
            showErrorAlert("Failed to deactivate voucher");
          }
        } else {
          const voucherToUpdate = vouchers.find((v) => v.voucherId === voucherId);
          if (voucherToUpdate) {
            const updateData = {
              voucherId: voucherToUpdate.voucherId,
              code: voucherToUpdate.code,
              description: voucherToUpdate.description,
              discountAmount: voucherToUpdate.discountAmount,
              isPercentage: voucherToUpdate.isPercentage,
              maxUsageCount: voucherToUpdate.maxUsageCount,
              startDate: voucherToUpdate.startDate,
              endDate: voucherToUpdate.endDate,
              isActive: true,
              productIds:
                voucherToUpdate.eligibleProducts?.map((ep) => ep.productId) || [],
            };

            const response = await authFetch(
              `${API_BASE_URL}/api/Vouchers/${voucherId}`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(updateData),
              }
            );

            if (response.ok) {
              showSuccessAlert("Voucher reactivated successfully!");

              const updatedVouchers = vouchers.map((v) =>
                v.voucherId === voucherId ? { ...v, isActive: true } : v
              );
              setVouchers(updatedVouchers);
              setFilteredVouchers(updatedVouchers);
            } else {
              showErrorAlert("Failed to reactivate voucher");
            }
          }
        }
      } catch (err) {
        showErrorAlert("Error updating voucher status: " + err.message);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleBack = () => {
    navigate("/admin/dashboard");
  };

  // Get category name by ID
  const getCategoryNameById = (categoryId) => {
    const category = categories.find((c) => c.categoryID === categoryId);
    return category ? category.name : "Unknown";
  };

  // Dual Grid Product Selector component
  const renderProductGridSelector = () => {
    return (
      <div className="admin-voucher-dual-grid">
        <div className="admin-voucher-grid-container">
          {/* Filter controls */}
          <div className="admin-voucher-grid-filters">
            <div className="admin-voucher-filter-group">
              <input
                type="text"
                placeholder="Filter by product name"
                value={nameFilter}
                onChange={handleNameFilterChange}
                className="admin-voucher-filter-input"
              />
            </div>

            <div className="admin-voucher-filter-group-right">
              <select
                value={categoryFilter}
                onChange={handleCategoryFilterChange}
                className="admin-voucher-filter-select"
                disabled={loadingCategories}
              >
                <option value="">All Categories</option>
                {categories
                  .filter(category => !category.subCategories || category.subCategories.length === 0)
                  .map((category) => (
                    <option key={category.categoryID} value={category.categoryID}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>

            <button
              type="button"
              onClick={handleClearFilters}
              className="admin-voucher-filter-clear-btn"
              disabled={!nameFilter && !categoryFilter}
            >
              Clear Filters
            </button>
          </div>

          <div className="admin-voucher-grid-label">Available Products</div>
          <div className="admin-voucher-grid">
            {availableList.length === 0 ? (
              <div className="admin-voucher-grid-empty">
                {nameFilter || categoryFilter
                  ? "No products match your filters"
                  : "No products available"}
              </div>
            ) : (
              availableList.map((product) => (
                <div
                  key={product.productID}
                  className={`admin-voucher-grid-item ${highlightedAvailable.includes(product.productID)
                    ? "highlighted"
                    : ""
                    }`}
                  onClick={(e) =>
                    handleAvailableItemClick(product.productID, e)
                  }
                >
                  <div className="admin-voucher-grid-item-name">
                    {product.name}
                  </div>
                  <div className="admin-voucher-grid-item-category">
                    {getCategoryNameById(product.categoryID)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="admin-voucher-grid-controls">
          <button
            type="button"
            className="admin-voucher-grid-btn"
            onClick={handleAddProductClick}
            disabled={highlightedAvailable.length === 0}
          >
            Add &gt;&gt;
          </button>
          <button
            type="button"
            className="admin-voucher-grid-btn"
            onClick={handleAddAllProductsClick}
            disabled={availableList.length === 0}
          >
            Add All &gt;&gt;
          </button>
          <button
            type="button"
            className="admin-voucher-grid-btn"
            onClick={handleRemoveProductClick}
            disabled={highlightedSelected.length === 0}
          >
            &lt;&lt; Remove
          </button>
          <button
            type="button"
            className="admin-voucher-grid-btn"
            onClick={handleRemoveAllProductsClick}
            disabled={selectedList.length === 0}
          >
            &lt;&lt; Remove All
          </button>
        </div>

        <div className="admin-voucher-grid-container-right">
          <div className="admin-voucher-grid-label">Selected Products</div>
          <div className="admin-voucher-grid">
            {selectedList.length === 0 ? (
              <div className="admin-voucher-grid-empty">
                No products selected
              </div>
            ) : (
              selectedList.map((product) => (
                <div
                  key={product.productID}
                  className={`admin-voucher-grid-item ${highlightedSelected.includes(product.productID)
                    ? "highlighted"
                    : ""
                    }`}
                  onClick={(e) => handleSelectedItemClick(product.productID, e)}
                >
                  <div className="admin-voucher-grid-item-name">
                    {product.name}
                  </div>
                  <div className="admin-voucher-grid-item-category">
                    {getCategoryNameById(product.categoryID)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-voucher-container">
      {/* Back to Dashboard */}
      <div className="admin-voucher-header">
        <button className="admin-voucher-back-btn" onClick={handleBack}>
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-voucher-title">
        <h1>Voucher Management</h1>
      </div>

      {/* Create Voucher Button - Moved above search bar */}
      <div className="admin-voucher-top-actions">
        <button
          className="admin-voucher-add-btn"
          onClick={handleAddClick}
        >
          <i className="fas fa-plus-circle"></i> Create New Voucher
        </button>
      </div>

      {/* Voucher Search Bar */}
      <div className="admin-voucher-search-container">
        <div className="admin-voucher-search-row">
          <div className="admin-voucher-search-group">
            <label htmlFor="voucherSearch">Search</label>
            <input
              id="voucherSearch"
              type="text"
              placeholder="Search vouchers by code or description..."
              value={voucherSearch}
              onChange={(e) => setVoucherSearch(e.target.value)}
              className="admin-voucher-search-input"
            />
          </div>
          <div className="admin-voucher-search-group">
            <label htmlFor="statusFilter">Status</label>
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="admin-voucher-filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="admin-voucher-search-actions">
            <button
              className="admin-voucher-reset-btn"
              onClick={resetSearchFilters}
            >
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Modal Section */}
      {showModal && (
        <div className="admin-voucher-modal-overlay">
          <div className="admin-voucher-modal">
            <div className="admin-voucher-modal-header">
              <h2>{isAddMode ? "Create New Voucher" : "Edit Voucher"}</h2>
              <button
                className="admin-voucher-modal-close"
                onClick={handleCancelClick}
              >
                &times;
              </button>
            </div>
            <div className="admin-voucher-modal-content">
              <form onSubmit={handleSubmit} className="admin-voucher-form">
                <div className="admin-voucher-form-group">
                  <label htmlFor="code">
                    Voucher Code
                    <span className="admin-voucher-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </div>

                <div className="admin-voucher-form-group">
                  <label htmlFor="description">
                    Description
                    <span className="admin-voucher-required">*</span>
                  </label>
                  <textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                  />
                </div>

                <div className="admin-voucher-form-row">
                  <div className="admin-voucher-form-group">
                    <label htmlFor="discountAmount">
                      Discount Amount
                      <span className="admin-voucher-required">*</span>
                    </label>
                    <input
                      type="number"
                      id="discountAmount"
                      value={discountAmount}
                      onChange={(e) => setDiscountAmount(e.target.value)}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="admin-voucher-form-group">
                    <label>Discount Type</label>
                    <span className="admin-voucher-help-text">
                      {isPercentage
                        ? "Discount will be applied as a percentage of the total"
                        : "Discount will be applied as a fixed amount"}
                    </span>
                    <div className="admin-voucher-checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={isPercentage}
                          onChange={(e) => setIsPercentage(e.target.checked)}
                        />
                        Is Percentage
                      </label>
                    </div>
                  </div>
                </div>

                <div className="admin-voucher-form-group">
                  <label htmlFor="minimumSpendAmount">Minimum Spend Amount</label>
                  <input
                    type="number"
                    id="minimumSpendAmount"
                    value={minimumSpendAmount}
                    onChange={(e) => setMinimumSpendAmount(e.target.value)}
                    step="0.01"
                    min="0"
                  />
                  <span className="admin-voucher-help-text">
                    {minimumSpendAmount
                      ? `Customers must spend at least RM${parseFloat(minimumSpendAmount).toFixed(2)} to use this voucher`
                      : "Leave empty for no minimum spend amount"}
                  </span>
                </div>

                <div className="admin-voucher-form-group">
                  <label htmlFor="maxUsageCount">
                    Maximum Usage Count
                    <span className="admin-voucher-required">*</span>
                  </label>
                  <input
                    type="number"
                    id="maxUsageCount"
                    value={maxUsageCount}
                    onChange={(e) => setMaxUsageCount(e.target.value)}
                    min="1"
                    required
                  />
                  <span className="admin-voucher-help-text">
                    Total uses across all customers. Each user account can use this voucher code once only.
                  </span>
                </div>

                <div className="admin-voucher-form-row">
                  <div className="admin-voucher-form-group">
                    <label htmlFor="startDate">Start Date</label>
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="admin-voucher-form-group">
                    <label htmlFor="endDate">End Date</label>
                    <input
                      type="date"
                      id="endDate"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div className="admin-voucher-form-group">
                  <label className="admin-dual-grid-voucher">
                    Eligible Products
                    <span className="admin-voucher-required">*</span>
                  </label>
                  {/* Render the dual grid selector */}
                  {renderProductGridSelector()}
                  {/* <span className="admin-voucher-help-text">
                    Hold Ctrl (or Cmd on Mac) to select multiple products at once.
                    At least one product must be selected for the voucher.
                  </span> */}
                </div>

                <div className="admin-voucher-form-actions">
                  <button
                    type="button"
                    className="admin-voucher-cancel-btn"
                    onClick={handleCancelClick}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="admin-voucher-save-btn">
                    {" "}
                    {isAddMode ? "Create Voucher" : "Update Voucher"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      {loading ? (
        <div className="admin-voucher-loading">
          <i className="fas fa-spinner fa-spin"></i> Loading vouchers...
        </div>
      ) : (
        <div className="admin-voucher-table-container">
          {filteredVouchers.length === 0 ? (
            <div className="admin-voucher-empty-container">
              <i className="fas fa-file-contract"></i>
              <p >No voucher found matching your criteria.</p>
            </div>
          ) : (
            <table className="admin-voucher-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Discount</th>
                  <th>Min Spend</th>
                  <th>Usage</th>
                  <th>Valid Period</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredVouchers.map((voucher) => (
                  <tr
                    key={voucher.voucherId}
                    className={!voucher.isActive ? "inactive-voucher" : ""}
                  >
                    <td>{voucher.code}</td>
                    <td className="admin-voucher-description-cell">
                      {voucher.description}
                    </td>
                    <td>
                      {voucher.isPercentage
                        ? `${voucher.discountAmount}%`
                        : `RM${voucher.discountAmount.toFixed(2)}`}
                    </td>
                    <td>
                      <span className={`admin-voucher-min-spend ${!voucher.minimumSpendAmount ? 'none' : ''}`}>
                        {voucher.minimumSpendAmount
                          ? `RM${voucher.minimumSpendAmount.toFixed(2)}`
                          : 'None'}
                      </span>
                    </td>
                    <td>
                      {voucher.currentUsageCount} / {voucher.maxUsageCount}
                    </td>
                    <td>
                      {formatDate(voucher.startDate)} - {formatDate(voucher.endDate)}
                    </td>
                    <td>
                      <span
                        className={`admin-voucher-status-badge ${voucher.isActive ? "active" : "inactive"
                          }`}
                      >
                        {voucher.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div className="admin-voucher-action-buttons">
                        <button
                          className="admin-voucher-edit-btn"
                          onClick={() => handleEditClick(voucher)}
                          disabled={!voucher.isActive}
                        >
                          <i className="fas fa-edit"></i> Edit
                        </button>
                        <button
                          className={
                            voucher.isActive
                              ? "admin-voucher-deactivate-btn"
                              : "admin-voucher-activate-btn"
                          }
                          onClick={() =>
                            handleToggleActive(
                              voucher.voucherId,
                              voucher.isActive
                            )
                          }
                        >
                          <i
                            className={`fas fa-${voucher.isActive ? "ban" : "check-circle"
                              }`}
                          ></i>
                          {voucher.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminVouchers;
